import type { CSSProperties } from "react";
import { eventNames, valueNames } from "@/modules/Constants";
import { findAskConfig, isPixieOpen } from "@/modules/pixieWorkspace";
import { pixieIntentProps } from "@/components/shopwindow/ask/pixieChunk";
import ChatAiIcon from "@svg/chat_ai.svg";
import type { ExperienceState } from "@/types/state";

const REGION = "Pixie Switch";

type PixieSwitchProps = {
  experienceState: ExperienceState;
  onApplicationEvent: (event: Record<string, unknown>) => void;
  className?: string;
  style?: CSSProperties;
};

// The Recipe | Pixie switch: Pixie's permanent way in and the way back. It stays
// in one place in both states; Experience positions it per layout.
export default function PixieSwitch({
  experienceState,
  onApplicationEvent,
  className,
  style,
}: PixieSwitchProps) {
  const onRecipe = () => {
    const applicationData = experienceState.applicationData;
    if (!isPixieOpen(applicationData)) {
      return;
    }
    onApplicationEvent({
      name: eventNames.SHOP_WINDOW_EXIT,
      region: REGION,
      shopWindowConfig: applicationData.getValue(valueNames.SHOP_WINDOW_CONFIG),
    });
  };

  const onPixie = () => {
    const applicationData = experienceState.applicationData;
    const config = findAskConfig(applicationData);
    if (!config || isPixieOpen(applicationData)) {
      return;
    }
    onApplicationEvent({
      name: eventNames.SET_SHOP_WINDOW,
      region: REGION,
      shopWindowConfig: config,
    });
  };

  const applicationData = experienceState.applicationData;
  if (!findAskConfig(applicationData)) {
    return null;
  }
  const open = isPixieOpen(applicationData);
  return (
    <div
      className={
        'pixie-switch tw:[font-family:"DM_Sans","Helvetica","Arial",sans-serif] tw:[-webkit-font-smoothing:antialiased]' +
        (open ? " pixie-switch--pixie" : "") +
        (className ? ` ${className}` : "")
      }
      style={style}
    >
      <div
        className="pixie-switch__track tw:relative tw:box-border tw:flex tw:rounded-[999px] tw:p-[4px] tw:[.pixie-switch--bar_&]:h-[40px] tw:[.pixie-switch--bar_&]:[background:#efefef]"
        role="group"
        aria-label="Recipe or Pixie"
      >
        <span
          className="pixie-switch__thumb tw:absolute tw:top-[4px] tw:bottom-[4px] tw:rounded-[999px]"
          aria-hidden="true"
        />
        <button
          type="button"
          aria-pressed={!open}
          className="pixie-switch__tab pixie-switch__tab--recipe tw:relative tw:flex-[1_1_0] tw:items-center tw:justify-center tw:gap-[6px] tw:rounded-[999px] tw:[font-weight:700]"
          onClick={onRecipe}
        >
          Recipe
        </button>
        <button
          type="button"
          aria-pressed={open}
          className="pixie-switch__tab pixie-switch__tab--pixie tw:relative tw:flex-[1_1_0] tw:items-center tw:justify-center tw:gap-[6px] tw:rounded-[999px] tw:[font-weight:700]"
          onClick={onPixie}
          {...pixieIntentProps}
        >
          <ChatAiIcon
            className="pixie-icon pixie-switch__icon"
            aria-hidden="true"
          />
          Pixie
        </button>
      </div>
    </div>
  );
}
